/** @jsx jsx */

import { Fragment as _, useState, useRef, useEffect, useContext } from 'react';
import { css, jsx, keyframes } from '@emotion/core';
import pubSub from 'services/pubSub';
import { UPDATE_RECTANGLE_HOTSPOT_LOCAL_STATE } from 'components/Editor/EditHotSpotIsolated/EditHotSpotIsolated_v2';
import { HotSpotTickedWhenSeen } from './HotSpotTickedWhenSeen';
import hexToRGB from 'services/hexToRGB';

const Rectangle = props => {
  const {
    id,
    name,
    currentHotspotId,
    root,
    inverseScale,
    hasBeenSeen,
    showSeenState,
    hotSpotFocus,
    color,
    points,
    immediateFeedback = true,
    isCorrect = false,
    enabled = true,
    showChecked,
    onClick,
    rectangleBorderRadius = 0,
    rectangleDisplayOutlineOnly,
    hotSpotsVisible,
    submitState,
    correctAnswers,
  } = props;

  const [hover, setHover] = useState(false);

  // const selected = id === `hotspot-${currentHotspotId}`;
  const selected = !immediateFeedback ? props?.selected : null;

  const [rectState, setRectState] = useState<{
    x: number;
    y: number;
    w: number;
    h: number;
  }>({
    x: 0,
    y: 0,
    w: 0,
    h: 0,
    ...points,
  });

  const drawing = useRef<boolean>(false);
  const startXY = useRef<{ startX: number; startY: number }>({
    startX: 0,
    startY: 0,
  });

  const { r, g, b } = hexToRGB(color);

  const getParsedColor = opacity => {
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  useEffect(() => {
    //
    const pS = pubSub.subscribe(
      `${UPDATE_RECTANGLE_HOTSPOT_LOCAL_STATE}_${id}`,
      points => {
        setRectState(points);
      }
    );

    return () => {
      pS();
    };
  }, []);

  const visibility = window.dev() ? true : hotSpotsVisible;

  const tickPosition = {
    x: rectState.x + rectState.w - 2.5,
    y: rectState.y + 2.5,
  };

  return (
    <svg>
      <rect
        key={`rect${0}`}
        id={id}
        rx={rectangleBorderRadius}
        x={rectState.x}
        y={rectState.y}
        width={rectState.w}
        height={rectState.h}
        style={{
          fill: visibility
            ? getParsedColor(rectangleDisplayOutlineOnly ? 1 : 0.4)
            : 'transparent',
          ...(rectangleDisplayOutlineOnly && {
            fill: 'transparent',
            stroke: color,
            strokeWidth: 3 * inverseScale,
          }),

          ...(!immediateFeedback &&
            showChecked && {
              fill: 'transparent',
              stroke: color,
              strokeWidth: 3 * inverseScale,
            }),

          cursor: 'pointer',
          opacity: hover ? 0.5 : 1,

          ...(`hotspot-${hotSpotFocus}` === id && {
            fill: 'transparent',
            stroke: color,
            strokeWidth: (rectangleDisplayOutlineOnly ? 6 : 3) * inverseScale,
          }),

          ...(!window.dev() && {
            transition: 'all 0.2s',
          }),

          pointerEvents: !immediateFeedback
            ? enabled
              ? 'auto'
              : 'none'
            : 'auto',

          // we'll stamp this in here as an ' override '
          // so as not to futz with the logic above that works
          // if we're a quiz. and selected. kill pointer
          ...(!immediateFeedback &&
            correctAnswers?.length === 1 &&
            selected && {
              pointerEvents: 'none',
            }),
        }}
        onMouseOver={() => setHover(true)}
        onMouseOut={() => setHover(false)}
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();
          onClick();
        }}
      />

      {immediateFeedback && showChecked && (
        <HotSpotTickedWhenSeen
          x={tickPosition.x}
          y={tickPosition.y}
          size={75 * inverseScale}
          color={color}
          inverseScale={inverseScale}
          displayAsRadio={submitState && submitState !== 'resolved'}
        />
      )}
    </svg>
  );
};

export default Rectangle;
